import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, ArrowLeft, Landmark, TrendingUp, GraduationCap, Trees, UtensilsCrossed, Info, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AddressSearch from "../components/AddressSearch";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { usePageSeo } from "../lib/seo";
import { slugify } from "../lib/slug";
import { CITY_DATA } from "./CityLanding";
import { getNeighborhoodCopy } from "../data/neighborhoodCopy";
import { getTaxMeasure, buildAvmFaq } from "../data/avmContent";
import RecentActivityStrip from "../components/valuation/RecentActivityStrip";
import MultiAiAvmSection from "../components/valuation/MultiAiAvmSection";
import { ENTITY_SITE, brokerageNode, agentNode, breadcrumbNode } from "../lib/entityGraph";

const SITE = ENTITY_SITE;
const money = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

function StatCard({ icon: Icon, label, value, note }) {
  return (
    <div className="rounded-2xl bg-white border border-brand-hairline p-6 shadow-[0_4px_20px_rgba(11,30,140,0.04)]" data-testid="hood-stat-card">
      <Icon className="h-5 w-5 text-brand-gold" />
      <p className="mt-4 text-xs uppercase tracking-wider text-brand-stone">{label}</p>
      <p className="mt-1 font-mono text-2xl font-bold text-brand-emerald">{value}</p>
      {note && <p className="mt-2 text-xs text-brand-stone/70 leading-relaxed">{note}</p>}
    </div>
  );
}

export default function NeighborhoodLanding() {
  const { citySlug, hoodSlug } = useParams();
  const city = CITY_DATA[citySlug];
  const hood = city?.savings?.find(([area]) => slugify(area) === hoodSlug);

  const canonical = `${SITE}/sell/${citySlug}/${hoodSlug}`;
  const area = hood?.[0];
  const zip = hood?.[1];
  const drivers = hood?.[2];
  const typical = hood?.[3] || 0;
  const taxMeasure = getTaxMeasure(citySlug);

  const faq = hood ? [
    [`What is the transfer tax rate when selling a home in ${area}, ${city.name}?`,
      `As part of ${city.name}, ${area} follows the same transfer-tax rules. ${city.tax}`],
    [`How much equity does the 1% listing fee save in ${area}?`,
      `On a representative ${money(typical)} ${area} sale, a 1% listing fee is ${money(typical * 0.01)} versus ${money(typical * 0.03)} at a traditional 3% listing side — roughly ${money(typical * 0.02)} more equity (2.0% of the sale price) retained before buyer-side compensation and closing costs. All commissions are negotiable and are not set by law; comparison figures are illustrative assumptions.`],
    [`How accurate is an automated home value estimate in ${area} (${zip})?`,
      `Automated estimates in ${area} track recent nearby sales closely, but unique lots, remodels, and condition still move value. Our estimate shows a low–high range and the comparable sales behind it, and a broker review sharpens the figure for ${zip}.`],
    ...buildAvmFaq(`${area}, ${city.name}`, taxMeasure),
  ] : [];

  const seo = hood ? {
    title: `${area} Multi-AI 3-Portal AVM | Equitas Homes`,
    description: `${area} (${city.name}, ${zip}) home values via a Multi-AI & 3-Portal AVM — Zillow, Redfin, Homes.com plus a 3-model AI panel — with local transfer taxes and your 1% net equity.`,
    canonical,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "WebApplication", "@id": `${canonical}#app`, name: `Equitas ${area} Multi-AI & 3-Portal AVM Tool`, url: canonical,
          applicationCategory: "RealEstateApplication", operatingSystem: "Web",
          description: `A free ${area} (${city.name}) home-valuation tool that benchmarks a live Zillow Zestimate®, Redfin, and Homes.com against a three-model AI appraisal panel (Gemini, OpenAI, Claude), applies local transfer taxes, and shows net equity at a 1% listing fee.`,
          featureList: ["3-portal AVM benchmarking (Zillow, Redfin, Homes.com)", "Multi-model AI comparative market analysis", "Local transfer-tax calculation", "1% vs 3% net-equity comparison"],
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          creator: { "@id": `${SITE}/#brokerage` },
          areaServed: { "@type": "Place", name: `${area}, ${city.name}, CA`,
            containedInPlace: { "@type": "City", name: `${city.name}, CA`, sameAs: city.wiki,
              geo: { "@type": "GeoCoordinates", latitude: city.geo[0], longitude: city.geo[1] } } } },
        brokerageNode(),
        agentNode(),
        { "@type": "FAQPage", "@id": `${canonical}#faq`,
          mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
        breadcrumbNode(`${canonical}#breadcrumb`, [
          { name: "Home", item: `${SITE}/` },
          { name: "Sell", item: `${SITE}/sell` },
          { name: city.name, item: `${SITE}/sell/${citySlug}` },
          { name: area, item: canonical },
        ]),
      ],
    },
  } : { title: "Equitas Home Value", canonical: SITE };
  usePageSeo(seo);

  if (!city) return <Navigate to="/" replace />;
  if (!hood) return <Navigate to={`/sell/${citySlug}`} replace />;

  const siblings = city.savings.filter(([a]) => slugify(a) !== hoodSlug);
  const introCopy = getNeighborhoodCopy(citySlug, hoodSlug)
    || `${area} is a distinctive ${city.name} neighborhood in ZIP ${zip}, where local values are driven by ${drivers}.`;

  return (
    <div className="min-h-screen bg-brand-cream paper-grain flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero + valuation widget */}
        <section className="bg-brand-emerald text-brand-cream">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24">
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-1.5 text-xs text-brand-cream/60" data-testid="hood-breadcrumb" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-brand-cream">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to={`/sell/${citySlug}`} className="hover:text-brand-cream">{city.name}</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-brand-cream/90">{area}</span>
            </nav>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="eyebrow mt-5 block">{area} · {city.name} · Multi-AI · 3-Portal AVM</span>
              <h1 className="mt-4 font-serif font-light leading-[1.06] text-4xl sm:text-5xl lg:text-6xl">
                {area} Home Values<br />& <span className="text-brand-gold">1% Listing Fee</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base sm:text-lg text-brand-cream/75 leading-relaxed">
                {introCopy}
              </p>
              <p className="mt-3 max-w-2xl text-sm text-brand-cream/60 leading-relaxed">
                Get a free, address-specific estimate for {zip} and see the equity you keep with a full-service 1% listing fee.
              </p>
              <div className="mt-8 max-w-2xl">
                <AddressSearch large cityContext={city.name} chips={city.savings.map(([a]) => a)} placeholder={`Enter ${area} or ${city.name} address…`} />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Snapshot: pricing drivers, representative value, savings, tax */}
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
          <span className="eyebrow">{area} Snapshot</span>
          <h2 className="mt-2 font-serif text-3xl text-brand-ink">Selling in {area}</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            <StatCard icon={TrendingUp} label="Representative sale price" value={money(typical)} note={`Illustrative ${area} (${zip}) pricing anchor — actual value varies by home.`} />
            <StatCard icon={MapPin} label="Est. 1% vs 3% savings" value={money(typical * 0.02)} note={`Listing-side savings on a ~${money(typical)} sale with Equitas' 1% listing fee vs a traditional 3%.`} />
            <StatCard icon={Landmark} label="Local transfer / conveyance tax" value={city.name} note={city.tax} />
          </div>
          <div className="mt-6 rounded-2xl bg-white border border-brand-hairline p-6">
            <h3 className="font-serif text-xl text-brand-ink">What drives value in {area}</h3>
            <p className="mt-2 text-sm text-brand-stone leading-relaxed">{drivers}. These are the levers buyers pay up for here; condition, lot size, and school attendance zones fine-tune the final number, which our estimate and comparable sales make transparent.</p>
          </div>
        </section>

        {/* Multi-AI & 3-Portal AVM: AEO answer + methodology (blocks 2 & 3) */}
        <MultiAiAvmSection locationName={`${area}, ${city.name}`} taxMeasure={taxMeasure} />

        {/* Live recent market activity (RentCast MLS sale listings for the ZIP) */}
        <RecentActivityStrip zip={zip} areaLabel={area} />

        {/* Community context inherited from parent city */}
        <section className="bg-white border-y border-brand-hairline">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
            <span className="eyebrow">About the {city.name} area</span>
            <div className="mt-6 grid lg:grid-cols-3 gap-8">
              <div>
                <GraduationCap className="h-6 w-6 text-brand-gold" />
                <h3 className="mt-4 font-serif text-2xl text-brand-ink">Schools</h3>
                <p className="mt-2 text-sm font-medium text-brand-emerald">{city.schools.district}</p>
                <ul className="mt-2 space-y-1 text-sm text-brand-stone">
                  {city.schools.list.map((s) => <li key={s}>• {s}</li>)}
                </ul>
                <p className="mt-3 text-sm text-brand-stone leading-relaxed">{city.schools.impact}</p>
              </div>
              <div>
                <Trees className="h-6 w-6 text-brand-gold" />
                <h3 className="mt-4 font-serif text-2xl text-brand-ink">Parks & green spaces</h3>
                <ul className="mt-3 space-y-3">
                  {city.parks.map((p) => (
                    <li key={p.name} className="text-sm"><span className="font-medium text-brand-ink">{p.name}</span><span className="block text-brand-stone">{p.amenities}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <UtensilsCrossed className="h-6 w-6 text-brand-gold" />
                <h3 className="mt-4 font-serif text-2xl text-brand-ink">Dining & downtown</h3>
                <p className="mt-2 text-sm font-medium text-brand-emerald">{city.dining.corridor}</p>
                <ul className="mt-2 space-y-1 text-sm text-brand-stone">
                  {city.dining.spots.map((s) => <li key={s}>• {s}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Equitas CTA */}
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <span className="eyebrow">Keep More of Your Equity</span>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">Full-service in {area}, for a 1% listing fee</h2>
            <p className="mt-4 text-brand-stone leading-relaxed max-w-2xl">
              Equitas Homes provides complete listing representation — pricing strategy, professional marketing, negotiation and closing — for a 1% listing fee instead of a traditional 3%. On a {money(typical)} {area} sale that's roughly {money(typical * 0.02)} more equity kept.
            </p>
          </div>
          <Link to="/" data-testid="hood-cta" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-emerald px-7 py-4 font-semibold text-brand-cream hover:bg-brand-emeraldHover transition-colors">
            <MapPin className="h-4 w-4" /> Estimate my {area} home
          </Link>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-5 sm:px-8 pb-16">
          <span className="eyebrow">{area} Seller FAQ</span>
          <h2 className="mt-2 font-serif text-3xl text-brand-ink">Selling a home in {area}</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="hood-faq">
            {faq.map(([q, a], i) => (
              <AccordionItem key={i} value={`q${i}`} className="border-brand-hairline">
                <AccordionTrigger className="text-left font-serif text-xl text-brand-ink hover:text-brand-emerald">{q}</AccordionTrigger>
                <AccordionContent className="text-brand-stone text-sm leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-6 flex items-start gap-2 rounded-xl bg-brand-sand p-4">
            <Info className="h-4 w-4 text-brand-stone shrink-0 mt-0.5" />
            <p className="text-xs text-brand-stone leading-relaxed">Estimates are automated and for information only — not an appraisal or guaranteed sale price. School boundaries, taxes, and market figures change; verify current details before listing.</p>
          </div>

          <div className="mt-8">
            <Link to={`/sell/${citySlug}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-emerald hover:underline" data-testid="hood-parent-link">
              <ArrowLeft className="h-3.5 w-3.5" /> All {city.name} neighborhoods & market report
            </Link>
          </div>

          {siblings.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider text-brand-stone">Other {city.name} neighborhoods</p>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                {siblings.map(([a]) => (
                  <Link key={a} to={`/sell/${citySlug}/${slugify(a)}`} className="inline-flex items-center gap-1 rounded-full border border-brand-hairline px-4 py-2 text-brand-stone hover:border-brand-emerald hover:text-brand-emerald transition-colors">
                    {a} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
